import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import * as log from '../../util/log';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import api from '../../api';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { parseListingToSharetribe } from '../../util/sharetribeParser';

const { UUID } = sdkTypes;

const REVIEWS_PAGE_SIZE = 10;
const LISTINGS_PAGE_SIZE = 2;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

export const QUERY_REVIEWS_COUNT_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_COUNT_REQUEST';
export const QUERY_REVIEWS_COUNT_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_COUNT_SUCCESS';
export const QUERY_REVIEWS_COUNT_ERROR = 'app/ProfilePage/QUERY_REVIEWS_COUNT_ERROR';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userShowError: null,

  listings: [],
  listingsPagination: {},
  listingsQueryParams: {},
  queryListingsInProgress: false,
  queryListingsError: null,

  reviews: [],
  reviewsQueryParams: {},
  reviewsPagination: {},
  queryReviewsError: null,
  queryReviewsInProgress: false,

  totalHostReviews: 0,
  totalGuestsReviews: 0,
  queryReviewsCountError: null,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        listings: payload.userId === state.userId ? state.listings : [],
        queryListingsInProgress: true,
        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return {
        ...state,
        queryListingsInProgress: false,
        listings:
          payload.pagination.page > 1 ? [...state.listings, ...payload.listings] : payload.listings,
        listingsQueryParams: payload.queryParams,
        listingsPagination: payload.pagination,
      };
    case QUERY_LISTINGS_ERROR:
      return {
        ...state,
        listings: [],
        queryListingsInProgress: false,
        queryListingsError: payload,
      };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsInProgress: true, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return {
        ...state,
        queryReviewsInProgress: false,
        reviews:
          payload.pagination.page > 1 ? [...state.reviews, ...payload.reviews] : payload.reviews,
        reviewsQueryParams: payload.queryParams,
        reviewsPagination: payload.pagination,
      };
    case QUERY_REVIEWS_ERROR:
      return { ...state, queryReviewsInProgress: false, reviews: [], queryReviewsError: payload };
    case QUERY_REVIEWS_COUNT_REQUEST:
      return { ...state, queryReviewsCountError: null };
    case QUERY_REVIEWS_COUNT_SUCCESS:
      return {
        ...state,
        totalHostReviews:
          payload.type === 'ofCustomer' ? payload.totalItems : state.totalHostReviews,
        totalGuestsReviews:
          payload.type === 'ofProvider' ? payload.totalItems : state.totalGuestsReviews,
      };
    case QUERY_REVIEWS_COUNT_ERROR:
      return {
        ...state,
        totalHostReviews: 0,
        totalGuestsReviews: 0,
        queryReviewsCountError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = response => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: response,
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsCountRequest = () => ({
  type: QUERY_REVIEWS_COUNT_REQUEST,
});

export const queryReviewsCountSuccess = response => ({
  type: QUERY_REVIEWS_COUNT_SUCCESS,
  payload: response,
});

export const queryReviewsCountError = e => ({
  type: QUERY_REVIEWS_COUNT_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = response => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: response,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const queryUserListings = params => (dispatch, getState, sdk) => {
  const { userId, page = 1 } = params;

  const { partner } = getState().ui;

  dispatch(queryListingsRequest(userId));

  return api.listings
    .fetchListings(
      {
        'attributes.state': { $eq: LISTING_STATE_PUBLISHED },
        authorId: userId.uuid,
        $page: page,
        $limit: LISTINGS_PAGE_SIZE,
        $sort: '-createdAt',
      },
      partner?.key
    )
    .then(response => {
      const { items, ...paginationProps } = response.data;

      const pagination = {
        ...paginationProps,
        perPage: LISTINGS_PAGE_SIZE,
        page: paginationProps.currentPage,
      };
      const listings = items.map(listing => parseListingToSharetribe(listing, listing.author));

      const queryParams = { userId };

      dispatch(queryListingsSuccess({ listings, pagination, queryParams }));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const queryUserReviews = params => (dispatch, getState, sdk) => {
  const { userId, page = 1, type = 'ofCustomer' } = params;
  dispatch(queryReviewsRequest());

  sdk.reviews
    .query({
      subject_id: userId,
      type,
      per_page: REVIEWS_PAGE_SIZE,
      page,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const pagination = response.data.meta;
      const queryParams = { type, ...params };
      const reviews = denormalisedResponseEntities(response);
      dispatch(queryReviewsSuccess({ reviews, pagination, queryParams }));
    })
    .catch(e => dispatch(queryReviewsError(e)));
};

export const queryUserReviewsCount = userId => (dispatch, getState, sdk) => {
  sdk.reviews
    .query({
      subject_id: userId,
      type: 'ofCustomer',
      per_page: 1,
      state: 'public',
    })
    .then(response => {
      const pagination = response.data.meta;
      const { totalItems } = pagination;
      dispatch(queryReviewsCountSuccess({ type: 'ofCustomer', totalItems }));
    })
    .catch(e => dispatch(queryReviewsCountError(e)));

  sdk.reviews
    .query({
      subject_id: userId,
      type: 'ofProvider',
      per_page: 1,
      state: 'public',
    })
    .then(response => {
      const pagination = response.data.meta;
      const { totalItems } = pagination;
      dispatch(queryReviewsCountSuccess({ type: 'ofProvider', totalItems }));
    })
    .catch(e => dispatch(queryReviewsCountError(e)));
};

export const showUser = userId => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(showUserSuccess());
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

export const loadData = params => async (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  await Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(showUser(userId)),
    dispatch(queryUserReviewsCount(userId)),
    dispatch(queryUserListings({ userId })),
    dispatch(queryUserReviews({ userId })),
  ]);

  try {
    // If SSR, fetch all published listings for this user so the server
    // render includes anchor links for every listing (not only the first page).
    const isSSR = typeof window === 'undefined';
    if (isSSR) {
      let usedCache = false;
      
      // Try cache first
      try {
        // Access globally available cache (set by server at startup)
        if (global.listingsCache) {
          const cachedListings = global.listingsCache.getListingsByAuthor(userId.uuid);
          
          if (cachedListings && cachedListings.length > 0) {
            console.log(`✅ Profile SSR: Using ${cachedListings.length} cached listings for author ${userId.uuid}`);
            
            const pagination = {
              totalItems: cachedListings.length,
              totalPages: 1,
              perPage: cachedListings.length,
              page: 1,
            };

            const queryParams = { userId };
            dispatch(queryListingsSuccess({ listings: cachedListings, pagination, queryParams }));
            usedCache = true;
          }
        } else {
          console.log('⚠️ Profile SSR: Cache not available, using live API');
        }
      } catch (cacheError) {
        console.log('⚠️ Profile SSR: Cache error, using live API:', cacheError.message);
      }
      
      // Fallback to live API if cache didn't work
      if (!usedCache) {
        const state = getState();
        const { partner } = state.ui;
        const filters = {
          'attributes.state': { $eq: LISTING_STATE_PUBLISHED },
          // API expects authorId as plain UUID string (not SDK UUID object)
          authorId: userId.uuid,
          $page: 1,
          $limit: 1000,
          $sort: '-createdAt',
        };

        const response = await api.listings.fetchListings(filters, partner?.key);
        const { items, ...paginationProps } = response.data;
        const listings = items.map(listing => parseListingToSharetribe(listing, listing.author));

        const pagination = {
          ...paginationProps,
          perPage: listings.length,
          page: 1,
        };

        const queryParams = { userId };
        dispatch(queryListingsSuccess({ listings, pagination, queryParams }));
      }
    }
  } catch (e) {
    // Non-fatal: log and continue. UI will still render the first page results.
    log.error(e, 'profilepage-ssr-fetch-all-listings-failed', { userId: params.id });
  }
  

  // Continue with the rest of the original logic
  const state = getState();

  // SEO INDEXING CHECK: User profiles should only be indexed if:
  // 1. User has Stripe verification (metadata.isStripeVerified === true)
  // 2. User has at least 1 published listing

  const { partner } = state.ui;
  const listings = state.ProfilePage.listings || [];

  // Get user data from marketplace entities
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const isStripeVerified = user?.attributes?.profile?.metadata?.isStripeVerified === true;

  const hasPublishedListings = listings.length > 0;
  const isEligibleForIndexing = isStripeVerified && hasPublishedListings;

  // PARTNER SUBDOMAIN: Return 410 Gone if user is not eligible
  // 410 = Permanent unavailability → Search engines remove from index faster
  if (partner && partner.key && !isEligibleForIndexing) {
    const reason = !isStripeVerified ? 'User is not Stripe verified' : 'User has no listings for this partner';

    const error = new Error(reason);
    error.status = 410;
    error.statusText = 'Gone';
    throw error;
  }

  // MAIN DOMAIN (crion.org): Don't throw error, let renderer.js handle robots meta tag
  // The page will render but robots.js will check eligibility and set noindex if needed
  // We store eligibility in state for potential use by renderer
  if (!isEligibleForIndexing) {
    // Log for debugging (SSR only)
    if (typeof window === 'undefined') {
      console.log('[SSR] Profile not eligible for indexing:', {
        isStripeVerified,
        hasPublishedListings,
        userId: userId.uuid,
      });
    }
  }
};
